import {RequestQueryBuilder, SCondition} from '@nestjsx/crud-request';
import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {BehaviorSubject, combineLatest, Observable, of, ReplaySubject} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {filter, map, shareReplay, switchMap, tap} from 'rxjs/operators';
import {TorchAPIConfiguration} from '../../../config/api.configuration';
import {TorchUser, UserPermissions} from '../models';
import {Burly} from 'kb-burly';
import {NgxPermissionsService} from 'ngx-permissions';
import {TypedPaginationResponse} from '../../../responses';
import {Angulartics2} from 'angulartics2';
import {TorchTokenService} from './torch-token.service';
import {environment} from '../../../../environments/environment';
import {UserPermissionsResponse} from '../responses';
import {LoggingService} from '../../../../app/shared/services/logging.service';
import {MsalBroadcastService} from '@azure/msal-angular';
import {EventType} from '@azure/msal-browser';
import {Cacheable} from 'ts-cacheable';

@Injectable({
  providedIn: 'root'
})
export class TorchUserService {
  public currentTorchUser: ReplaySubject<TorchUser> = new ReplaySubject<TorchUser>();
  public currentUserPermissions: Observable<UserPermissions[]>;

  private  _currentTorchUser: Observable<TorchUser>;
  private _hasTorchUser: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private hasSetMetrics = false;
  private hasLoadedPermissions = false;

  constructor(protected config: TorchAPIConfiguration,
              protected httpClient: HttpClient,
              private router: Router,
              protected angulartics2: Angulartics2,
              private broadcastService: MsalBroadcastService,
              private tokenService: TorchTokenService,
              private logger: LoggingService,
              private permissionService: NgxPermissionsService) {

    // Setup an Observable to watch for _hasTorchUser and getSignedInUser
    this._currentTorchUser = combineLatest([this.getSignedInUser(), this._hasTorchUser]).pipe(
      map(([user, hasUser]) => {
        if (hasUser || !!user) {
          this.updateMetrics(user);
          this.loadCurrentUserPermissions();

          return user;
        } else {
          this.logger.error('No current TORCH user');
        }
      })
    );

    // Watch the Observable
    this._currentTorchUser.pipe(filter(user => !!user)).subscribe(this.currentTorchUser);

    this.currentUserPermissions = combineLatest([this.getUserPermissions(), this._hasTorchUser]).pipe(
      map(([permissions, hasUser]) => {
        if (hasUser && permissions.length) {
          this.applyPermissionsToCurrentUser(permissions);
          return permissions;
        }
      })
    );


    this.broadcastService.msalSubject$.subscribe(event => {
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        this.getSignedInUser().subscribe(user => {
          this.currentTorchUser.next(user);
          this.updateMetrics(user);
          this.loadCurrentUserPermissions();
        });
      }
    });
  }

  @Cacheable()
  public getSignedInUser() {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/users')
      .addSegment('/get')
      .get;

    this.logger.log('Get signed in user');
    return this.getGraphToken().pipe(
      switchMap((token: string) => {
        if (!!token) {
          return this.httpClient.get<TorchUser>(endpointURL, {
            responseType: 'json',
            headers: {
              msalToken: token
            }
          });
        }
      }),
      tap((user) => {
        this._hasTorchUser.next(!!user);
        if (!!user) {
          this.currentTorchUser.next(user);
        }
      })
    );
  }

  @Cacheable()
  public getSignedInUserAvatar(): Observable<string> {
    const reader = new FileReader();
    return this.currentTorchUser.pipe(
      switchMap(torchUser => this.getProfilePhoto(torchUser.emailAddress)),
      switchMap((blob: Blob) => new Observable<string>(observer => {
        reader.addEventListener('load', () => {
          observer.next(reader.result.toString());
          observer.complete();
        }, false);

        if (!!blob) {
          reader.readAsDataURL(blob);
        }
      })),
      shareReplay(1)
    );
  }

  @Cacheable()
  public getUserAvatar(emailAddress: string): Observable<string> {
    const reader = new FileReader();

    return this.getProfilePhoto(emailAddress).pipe(
      switchMap((blob: Blob) => new Observable<string>(observer => {
        reader.addEventListener('load', () => {
          observer.next(reader.result.toString());
          observer.complete();
        }, false);

        if (!!blob) {
          reader.readAsDataURL(blob);
        }
      })),
      shareReplay(1)
    );
  }

  @Cacheable({maxAge: 1000})
  public getUserPermissions(email: string | null = null): Observable<UserPermissions[]> {
    return of(email).pipe(
      switchMap(userEmail => {
        if (!!userEmail) {
          return of(userEmail);
        }

        return this.currentTorchUser.pipe(
          map(user => user.username)
        );
      }),
      switchMap(userEmail => {
        const endpointURL = Burly(this.config.rootUrl).addSegment('/user-permissions')
          .addQuery('filter', `username||$eq||${userEmail}`)
          .get;

        return this.httpClient.get<UserPermissionsResponse>(endpointURL).pipe(
          map(response => response.data)
        );
      }),
      shareReplay(1)
    );
  }

  public applyPermissionsToCurrentUser(permissions: UserPermissions[]) {
    const perms = permissions.map((perm) => perm.permission);
    this.permissionService.loadPermissions(perms);
  }

  public loadCurrentUserPermissions(): void {
    if (!this.hasLoadedPermissions) {
      this.currentUserPermissions.subscribe(permissions => {
          this.permissionService.flushPermissions();
          this.applyPermissionsToCurrentUser(permissions);
          this.hasLoadedPermissions = true;
        }
      );
    }
  }

  public updatePermissions(rawRoles: string[], username: string) {
    const endpointURL = Burly(this.config.rootUrl).addSegment('/user-permissions')
      .addSegment('/update')
      .addSegment(`/${username}`)
      .get;

    return this.httpClient.post<UserPermissionsResponse>(endpointURL, rawRoles);
  }

  @Cacheable()
  public getRoles() {
    const endpointURL = Burly(this.config.rootUrl).addSegment('/user-permissions')
      .addSegment('/roles')
      .get;

    return this.httpClient.get<string[]>(endpointURL);
  }

  @Cacheable({maxAge: 30000})
  public getUser(emailAddress: string = null): Observable<TorchUser> {
    if (!!emailAddress) {
      const endpointURL = Burly(this.config.rootUrl)
        .addSegment('/users')
        .addSegment('/get')
        .addQuery('emailAddress', emailAddress)
        .get;

      return this.getGraphToken().pipe(
        switchMap((token: string) => {
          if (!!token) {
            return this.httpClient.get<TorchUser>(endpointURL, {
              responseType: 'json',
              headers: {
                msalToken: token
              }
            });
          }
        })
      );
    } else {
      return this.currentTorchUser;
    }
  }

  @Cacheable()
  public getUsersRequest(pageSize: number = 50,
                         currentPage: number = 0,
                         sortDirection: 'ASC' | 'DESC' | '' = 'DESC',
                         sortBy: string = 'id',
                         filters: SCondition | null): Observable<TypedPaginationResponse<TorchUser>> {
    const qb = RequestQueryBuilder.create();

    qb.setPage(currentPage + 1).setLimit(pageSize);

    if (!!sortDirection) {
      qb.sortBy({field: sortBy, order: sortDirection});
    }

    if (!!filters) {
      qb.search(filters);
    }

    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/users')
      .appendRawQueryString(qb.query(false))
      .get;


    return this.httpClient.get<TypedPaginationResponse<TorchUser>>(endpoint);
  }

  public updateMetrics(user: TorchUser) {
    if (!!user && !!user.employeeId && !!user.username && !this.hasSetMetrics) {
      this.logger.log('Updating metrics', user);
      this.angulartics2.setUsername.next(user.employeeId);
      if (!!user.manager) {
        this.angulartics2.setUserProperties.next({
          $email: user.username,
          USER_ID: user.employeeId,
          $name: user.displayName,
          location: user.workAddress,
          manager: user.manager.fullName,
          department: user.department,
          jobTitle: user.jobTitle,
          avatar: null,
          production: environment.production,
          staging: environment.staging,
          development: environment.development
        });
      } else {
        this.angulartics2.setUserProperties.next({
          $email: user.username,
          USER_ID: user.employeeId,
          $name: user.displayName,
          location: user.workAddress,
          department: user.department,
          jobTitle: user.jobTitle,
          avatar: null,
          manager: null,
          production: environment.production,
          staging: environment.staging,
          development: environment.development
        });
      }

      this.hasSetMetrics = true;
    }
  }

  /**
   * Returns the blob of the user profile photo
   */
  @Cacheable()
  private getProfilePhoto(userEmail: string = null) {
    return this.getGraphToken().pipe(
      switchMap((token: string) => this.httpClient.get(this.getProfilePhotoURL(userEmail), {
        responseType: 'blob', headers: {
          msalToken: token
        }
      }))
    );
  }

  /**
   * Returns the URL for the user's photo
   *
   * @param userEmail
   */
  private getProfilePhotoURL(userEmail: string = null) {
    return Burly(this.config.filesUrl)
      .addSegment('/avatars')
      .addSegment('/photo')
      .addQuery('userEmail', userEmail)
      .get;
  }


  private getGraphToken(): Observable<string> {
    return this.tokenService.getTokens().pipe(
      filter(tokens => !!tokens && !!tokens.graphToken && !!tokens.torchToken),
      map(tokens => tokens.graphToken),
    );
  }
}
